import React, { useState } from 'react';
import {
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Divider,
    Box
} from '@mui/material';
import {
    PlayCircleOutline as TourIcon,
    Support as SupportIcon,
    BugReport as BugIcon,
    School as SchoolIcon,
    Class as ClassIcon,
    LibraryBooks as LibraryBooksIcon,
    LocalOffer as LocalOfferIcon,
    People as PeopleIcon,
    Business as BusinessIcon,
    Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import ContactModal from './ContactModal';
import ReportProblemModal from './ReportProblemModal';
import { TourType, allTours } from '../../config/TourConfigurations';

/**
 * Props for the TutorialMenu component
 */
interface TutorialMenuProps {
    /** Anchor element for the menu */
    anchorEl: HTMLElement | null;
    /** Whether the menu is open */
    open: boolean;
    /** Function to close the menu */
    onClose: () => void;
    /** Function to select a specific tour */
    onSelectTour: (tourType: TourType) => void;
}

const tourIcons: Record<TourType, React.ReactNode> = {
    os: <SchoolIcon />,
    cursus: <ClassIcon />,
    cours: <LibraryBooksIcon />,
    marketing: <LocalOfferIcon />,
    users: <PeopleIcon />,
    organizations: <BusinessIcon />,
    enrollment: <AssignmentIcon />,
    monof: <BusinessIcon />
};

// Map tour types to app IDs in enabledApps
const tourToAppMap: Record<TourType, string | null> = {
    os: null, // Always available
    cursus: 'cursus',
    cours: 'courses',
    marketing: 'marketing',
    users: 'users',
    organizations: 'organizations',
    enrollment: 'enrollment',
    monof: 'monof'
};

/**
 * Menu component for accessing tutorials, guides, and support.
 * Displays available tours based on user permissions and provides access to help resources.
 *
 * @param props - The props for the component
 * @returns The Tutorial Menu UI
 */
const TutorialMenu: React.FC<TutorialMenuProps> = ({ anchorEl, open, onClose, onSelectTour }) => {
    const { user } = useAuth();
    const [contactOpen, setContactOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [contactType, setContactType] = useState<'help' | 'bug'>('help');

    const handleSelectTour = (tourType: TourType) => {
        onSelectTour(tourType);
        onClose();
    };

    const handleOpenContact = (type: 'help' | 'bug') => {
        setContactType(type);
        setContactOpen(true);
        onClose();
    };

    const handleOpenReport = () => {
        setReportOpen(true);
        onClose();
    };

    // Filter tours based on user's authorized and enabled apps
    const availableTours = allTours.filter(tour => {
        const appId = tourToAppMap[tour.id];
        // If no app mapping (like 'os'), always show
        if (!appId) return true;
        // Check if the app is both authorized and enabled
        // @ts-ignore - Dynamic access to authorizedApps
        const isAuthorized = user?.authorizedApps?.[appId] ?? false;
        // @ts-ignore - Dynamic access to enabledApps
        const isEnabled = user?.preferences?.enabledApps?.[appId] ?? true;
        return isAuthorized && isEnabled;
    });

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: 320,
                        maxHeight: 500
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
                <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    <Typography variant="h6">Centre d'aide</Typography>
                    <Typography variant="body2">Tutoriels et assistance</Typography>
                </Box>

                <Divider />

                <Typography variant="overline" sx={{ px: 2, py: 1, display: 'block' }}>
                    Tutoriels disponibles
                </Typography>

                {availableTours.map((tour) => (
                    <MenuItem key={tour.id} onClick={() => handleSelectTour(tour.id)}>
                        <ListItemIcon>
                            {tourIcons[tour.id]}
                        </ListItemIcon>
                        <ListItemText primary={tour.name} secondary={tour.description} />
                    </MenuItem>
                ))}

                <Divider />

                <MenuItem onClick={() => handleOpenContact('help')}>
                    <ListItemIcon>
                        <SupportIcon />
                    </ListItemIcon>
                    <ListItemText primary="Contacter le support" secondary="Besoin d'aide ?" />
                </MenuItem>

                <MenuItem onClick={handleOpenReport}>
                    <ListItemIcon>
                        <BugIcon />
                    </ListItemIcon>
                    <ListItemText primary="Signaler un problème" secondary="Bug ou erreur technique" />
                </MenuItem>
            </Menu>

            <ContactModal
                open={contactOpen}
                onClose={() => setContactOpen(false)}
                type={contactType}
            />

            <ReportProblemModal
                open={reportOpen}
                onClose={() => setReportOpen(false)}
            />
        </>
    );
};

export default TutorialMenu;
